import { create } from 'zustand';
import { roleService, Role, CreateRoleDto, UpdateRoleDto, CreatePermissionRoleDto, UpdatePermissionRoleDto } from '@/services/roleService';

interface RoleState {
    // Data state
    roles: Role[];
    selectedRole: Role | null;
    rolePermissions: any[];

    // Loading states
    isLoading: boolean;
    isLoadingDetail: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;

    // Error states
    error: string | null;
    detailError: string | null;

    // Actions
    fetchRoles: () => Promise<void>;
    fetchRoleById: (id: string) => Promise<void>;
    fetchPermissionsByRoleId: (id: string) => Promise<void>;
    createRole: (data: CreateRoleDto) => Promise<boolean>;
    createPermissionRole: (data: CreatePermissionRoleDto) => Promise<boolean>;
    updateRole: (id: string, data: UpdateRoleDto) => Promise<boolean>;
    updatePermissionRole: (id: string, data: UpdatePermissionRoleDto) => Promise<boolean>;
    deleteRole: (id: string) => Promise<boolean>;
    clearSelectedRole: () => void;
    clearError: () => void;
}

export const useRoleStore = create<RoleState>()((set, get) => ({
    // Initial state
    roles: [],
    selectedRole: null,
    rolePermissions: [],

    isLoading: false,
    isLoadingDetail: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,

    error: null,
    detailError: null,

    // Actions
    fetchRoles: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await roleService.getAllRolesWithCount();
            if (response.ok && response.data) {
                const data: any = response.data;
                set({
                    roles: data?.data?.data || [],
                    isLoading: false
                });
            } else {
                const data: any = response.data;
                set({
                    error: data?.message || 'Lỗi khi tải danh sách vai trò',
                    isLoading: false
                });
            }
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi tải danh sách vai trò',
                isLoading: false
            });
        }
    },

    fetchRoleById: async (id) => {
        set({ isLoadingDetail: true, detailError: null });
        try {
            const response = await roleService.getRoleById(id);
            if (response.ok && response.data) {
                const data: any = response.data;
                set({
                    selectedRole: data.data || null,
                    isLoadingDetail: false
                });
            } else {
                const data: any = response.data;
                set({
                    detailError: data?.message || 'Lỗi khi tải thông tin vai trò',
                    isLoadingDetail: false
                });
            }
        } catch (error) {
            set({
                detailError: error instanceof Error ? error.message : 'Lỗi khi tải thông tin vai trò',
                isLoadingDetail: false
            });
        }
    },

    fetchPermissionsByRoleId: async (id) => {
        set({ isLoadingDetail: true, detailError: null });
        try {
            const response = await roleService.getPermissionsByRoleId(id);
            if (response.ok && response.data) {
                const data: any = response.data;
                console.log("sdfsd: ", data.data?.data)
                set({
                    rolePermissions: data.data?.data || [],
                    isLoadingDetail: false
                });
            } else {
                const data: any = response.data;
                set({
                    detailError: data?.message || 'Lỗi khi tải quyền hạn',
                    isLoadingDetail: false
                });
            }
        } catch (error) {
            set({
                detailError: error instanceof Error ? error.message : 'Lỗi khi tải quyền hạn',
                isLoadingDetail: false
            });
        }
    },

    createRole: async (data) => {
        set({ isCreating: true, error: null });
        try {
            const response = await roleService.createRole(data);
            set({ isCreating: false });

            if (response.ok && response.data) {
                // Refresh role list after creating
                get().fetchRoles();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi tạo vai trò mới',
                isCreating: false
            });
            return false;
        }
    },

    createPermissionRole: async (data) => {
        set({ isCreating: true, error: null });
        try {
            const response = await roleService.createPermissionRole(data);
            set({ isCreating: false });

            if (response.ok && response.data) {
                // Refresh role list after creating
                get().fetchRoles();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi tạo vai trò với quyền hạn',
                isCreating: false
            });
            return false;
        }
    },

    updateRole: async (id, data) => {
        set({ isUpdating: true, error: null });
        try {
            const response = await roleService.updateRole(id, data);
            set({ isUpdating: false });

            if (response.ok && response.data) {
                const data: any = response.data;
                // Update the selected role if it's the one being edited
                if (get().selectedRole?._id === id) {
                    set({ selectedRole: data.data || null });
                }

                // Refresh role list after updating
                get().fetchRoles();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi cập nhật vai trò',
                isUpdating: false
            });
            return false;
        }
    },

    updatePermissionRole: async (id, data) => {
        set({ isUpdating: true, error: null });
        try {
            const response = await roleService.updatePermissionsByRoleId(id, data);
            set({ isUpdating: false });

            if (response.ok && response.data) {
                const data: any = response.data;
                // Update the selected role if it's the one being edited
                if (get().selectedRole?._id === id) {
                    set({ selectedRole: data.data?.role || null });
                }

                // Refresh role list after updating
                get().fetchRoles();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi cập nhật quyền hạn',
                isUpdating: false
            });
            return false;
        }
    },

    deleteRole: async (id) => {
        set({ isDeleting: true, error: null });
        try {
            const response = await roleService.deleteRole(id);
            set({ isDeleting: false });

            if (response.ok && response.data) {
                // Clear selected role if it's the one being deleted
                if (get().selectedRole?._id === id) {
                    set({ selectedRole: null });
                }

                // Refresh role list after deleting
                get().fetchRoles();
                return true;
            }
            return false;
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : 'Lỗi khi xóa vai trò',
                isDeleting: false
            });
            return false;
        }
    },

    clearSelectedRole: () => set({ selectedRole: null, detailError: null, rolePermissions: [] }),
    clearError: () => set({ error: null, detailError: null }),
}));
